import { apiRequest, SUBSCRIPTION_ENDPOINTS, buildApiUrl } from '../config/api';

/**
 * Subscription Service
 * Handles all subscription-related API calls with automatic order and invoice creation
 */

class SubscriptionService {
  // =============================================================================
  // SUBSCRIPTION PLAN METHODS
  // =============================================================================

  /**
   * Get all available subscription plans
   */
  async getPlans() {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.PLANS, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  }

  /**
   * Get current user subscription
   */
  async getCurrentSubscription() {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.CURRENT, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      throw error;
    }
  }

  /**
   * Get subscription usage statistics
   */
  async getUsage() {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.USAGE, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching subscription usage:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive subscription summary including orders and invoices
   */
  async getSummary() {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.SUMMARY, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching subscription summary:', error);
      throw error;
    }
  }

  // =============================================================================
  // SUBSCRIPTION ACTION METHODS (WITH ORDER/INVOICE CREATION)
  // =============================================================================

  /**
   * Subscribe to a plan (creates order and invoice automatically)
   * @param {number} planId - The subscription plan ID
   */
  async subscribeToPlan(planId) {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.SUBSCRIBE, {
        method: 'POST',
        body: JSON.stringify({ plan_id: planId }),
      });
      return response;
    } catch (error) {
      console.error('Error subscribing to plan:', error);
      throw error;
    }
  }

  /**
   * Subscribe to a specific plan by ID (creates order and invoice automatically)
   * @param {number} planId - The subscription plan ID
   */
  async subscribeToSpecificPlan(planId) {
    try {
      const endpoint = buildApiUrl(SUBSCRIPTION_ENDPOINTS.SUBSCRIBE_TO_PLAN, {
        id: planId,
      });
      const response = await apiRequest(endpoint, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Error subscribing to specific plan:', error);
      throw error;
    }
  }

  /**
   * Upgrade subscription to a new plan (creates order and invoice automatically)
   * @param {number} newPlanId - The new subscription plan ID
   */
  async upgradeSubscription(newPlanId) {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.UPGRADE, {
        method: 'POST',
        body: JSON.stringify({ new_plan_id: newPlanId }),
      });
      return response;
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      throw error;
    }
  }

  /**
   * Renew current subscription (creates order and invoice automatically)
   */
  async renewSubscription() {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.RENEW, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Error renewing subscription:', error);
      throw error;
    }
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription() {
    try {
      const response = await apiRequest(SUBSCRIPTION_ENDPOINTS.CANCEL, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }

  // =============================================================================
  // SUBSCRIPTION ACTION HANDLERS (WITH UI FEEDBACK)
  // =============================================================================

  /**
   * Handle subscription action with comprehensive UI feedback
   * @param {string} action - The action type ('subscribe', 'upgrade', 'renew', 'cancel')
   * @param {Object} params - Action parameters
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   * @param {Function} onLoading - Loading state callback
   */
  async handleSubscriptionAction(
    action,
    params = {},
    onSuccess,
    onError,
    onLoading,
  ) {
    try {
      if (onLoading) onLoading(true);

      let response;
      let actionMessage = '';

      switch (action) {
        case 'subscribe':
          response = await this.subscribeToPlan(params.planId);
          actionMessage = 'subscription created';
          break;

        case 'upgrade':
          response = await this.upgradeSubscription(params.newPlanId);
          actionMessage = 'subscription upgraded';
          break;

        case 'renew':
          response = await this.renewSubscription();
          actionMessage = 'subscription renewed';
          break;

        case 'cancel':
          response = await this.cancelSubscription();
          actionMessage = 'subscription cancelled';
          break;

        default:
          throw new Error(`Unknown subscription action: ${action}`);
      }

      if (response.success) {
        // Prepare success data
        const successData = {
          message: response.message,
          subscription: response.subscription,
          orderId: response.order_id,
          invoiceId: response.invoice_id,
          totalAmount: response.total_amount,
          action: action,
          actionMessage: actionMessage,
        };

        if (onSuccess) onSuccess(successData);
        return successData;
      } else {
        const errorMessage =
          response.message || `Failed to ${action} subscription`;
        if (onError) onError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error.message || `An error occurred while ${action}ing subscription`;
      if (onError) onError(errorMessage);
      throw error;
    } finally {
      if (onLoading) onLoading(false);
    }
  }

  // =============================================================================
  // IMEI CHECK METHODS
  // =============================================================================

  /**
   * Perform IMEI check
   * @param {string} imei - The IMEI number to check
   */
  async performIMEICheck(imei) {
    try {
      const response = await apiRequest('/api/imei/check/', {
        method: 'POST',
        body: JSON.stringify({ imei }),
      });
      return response;
    } catch (error) {
      console.error('Error performing IMEI check:', error);
      throw error;
    }
  }

  /**
   * Get IMEI check history
   */
  async getIMEIHistory() {
    try {
      const response = await apiRequest('/api/imei/history/', {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching IMEI history:', error);
      throw error;
    }
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Format subscription data for display
   * @param {Object} subscription - Subscription object
   */
  formatSubscriptionData(subscription) {
    if (!subscription) return null;

    return {
      id: subscription.id,
      planName: subscription.plan?.name || 'Unknown Plan',
      planType: subscription.plan?.plan_type || 'unknown',
      price: subscription.plan?.price || 0,
      status: subscription.status,
      checksUsed: subscription.imei_checks_used || 0,
      checksRemaining: subscription.imei_checks_remaining || 0,
      checksLimit: subscription.plan?.imei_checks_limit || 0,
      startedAt: subscription.started_at,
      expiresAt: subscription.expires_at,
      usagePercentage: subscription.plan?.imei_checks_limit
        ? Math.round(
            (subscription.imei_checks_used /
              subscription.plan.imei_checks_limit) *
              100,
          )
        : 0,
    };
  }

  /**
   * Check if user can perform subscription action
   * @param {Object} subscription - Current subscription
   * @param {string} action - Action to check
   */
  canPerformAction(subscription, action) {
    if (!subscription) {
      return action === 'subscribe';
    }

    switch (action) {
      case 'subscribe':
        return subscription.status !== 'active';

      case 'upgrade':
        return subscription.status === 'active';

      case 'renew':
        return ['active', 'expired'].includes(subscription.status);

      case 'cancel':
        return subscription.status === 'active';

      default:
        return false;
    }
  }

  /**
   * Get subscription status display info
   * @param {string} status - Subscription status
   */
  getStatusInfo(status) {
    const statusMap = {
      active: { label: 'Active', color: 'green', icon: '✓' },
      inactive: { label: 'Inactive', color: 'gray', icon: '○' },
      expired: { label: 'Expired', color: 'red', icon: '✗' },
      cancelled: { label: 'Cancelled', color: 'orange', icon: '⊘' },
    };

    return statusMap[status] || { label: 'Unknown', color: 'gray', icon: '?' };
  }
}

// Create and export singleton instance
const subscriptionService = new SubscriptionService();
export default subscriptionService;
